import { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { format } from 'date-fns'
import { IEntrie, IEntrieDetails } from '../../../../../../../modules/API/Knisot/models/entrie'
import useDataFetcher from '../../../hooks/useFetchData'
import { IGrowerItem } from '../../../../../../../modules/API/Defaults/models/growers'
import { ICarItem } from '../../../../../../../modules/API/Defaults/models/cars'
import { convertToDate } from '../../../../../../../modules/convertDateString'
import { IWarhouseListItem } from '../../../../../../../modules/API/Defaults/models/warhouseList'
import { useKnisaConfig } from '../../config'
import { ITeudotTypeItem } from '../../../../../../../modules/API/Defaults/models/teudotTypes'
import useDebounce from '../../../../seller/Helpers/useDebounce'
import { Knisot } from '../../../../../../../modules/API/Knisot/knisot'
import { useTranslation } from 'react-i18next'
import { useBrodMigdal } from '../../../../../../../generic/hooks/useBrodMigdal'
import { toGroupID, filterByGroupID } from '../Helpers/filterByGroupID'

import GrowerSelector from '../../../../../../../generic/ui/Selectors/GrowerSelector'
import CarSelector from '../../../../../../../generic/ui/Selectors/CarSelector'
import WarehouseSelector from '../../../../../../../generic/ui/Selectors/WarehouseSelector'
import TeudaTypeSelector from '../../../../../../../generic/ui/Selectors/TeudaTypeSelector'
import TimeField from '../../../../../../../generic/ui/Input/TimeField'
import { TextInput } from '../../../../../../../generic/ui/Input/TextInput'
import DateField from '../../../../orders/Components/DateField'
import { AccentButton } from '../../../../../../../generic/ui/Button/AccentButton'
import {
  FormShell,
  FormGrid,
  FormField,
  FormFooter,
} from '../../../../../../../generic/ui/FormLayout'

interface IProps {
  fetchedKtisa: IEntrie | null
  growerList: IGrowerItem[]
  carList: ICarItem[]
  warehouseList: IWarhouseListItem[]
  createTitle: (data: IEntrieDetails) => void,
  teudaTypes: ITeudotTypeItem[],
  changeTitle: (data: IEntrie) => void
}

const parseYYYYMMDD = (s: string | undefined | null): Date | null => {
  if (!s || s.length < 8) return null
  const y = +s.slice(0, 4)
  const m = +s.slice(4, 6) - 1
  const d = +s.slice(6, 8)
  const date = new Date(y, m, d)
  if (isNaN(date.getTime())) return null
  return date
}

const ModalKnisaTitle = ({
  fetchedKtisa,
  growerList,
  carList,
  warehouseList,
  createTitle,
  teudaTypes,
  changeTitle
}: IProps) => {
  const [t] = useTranslation(['knisa', 'buttons'])
  const config = useKnisaConfig()
  const { getBrodMigdal } = useBrodMigdal()

  const initialDate = parseYYYYMMDD(fetchedKtisa?.EntryDate) ?? new Date()
  const isRequiredCar = config.create.title.car[0].value as boolean
  const isRequiredWarehouse = config.create.title.tranzit[0].value as boolean
  const defaultGrower = config.create.title.grower[0].value as number
  const defaultWarehouse = config.create.title.tranzit[1].value as number
  const isRequiredRef = config.create.title.ref[0].value as boolean
  const defaultSubTeuda = config.create.title.subTeuda[0].value as number

  const [date, setDate] = useState<Date>(initialDate)
  const [growerID, setGrowerID] = useState<number>(fetchedKtisa?.Grower || defaultGrower)
  const [carID, setCarID] = useState<number>(config.create.title.car[1].value as number)
  const [remark, setRemark] = useState<string>(fetchedKtisa?.Remark || '')
  const [warehouseID, setWarehouseID] = useState<number>(fetchedKtisa?.Tranzit || defaultWarehouse)
  const [refference, setRefference] = useState<number>(fetchedKtisa?.Reference || 0)
  const [leadName, setLeadName] = useState<string>(fetchedKtisa?.LeadName || '')
  const [carNum, setCarNum] = useState<number>(fetchedKtisa?.Transport || 0)
  const [time, setTime] = useState<string>(fetchedKtisa?.EntryTime || '')
  const [teudaTypeID, setTeudaTypeID] = useState<number>(fetchedKtisa?.TypeDoc || defaultSubTeuda || 0)
  const refferenceDebounced = useDebounce(refference, 1000)
  const canCheckRefference = !!refferenceDebounced && !!growerID
  const {
    data: dateRefference
  } = useDataFetcher<{Date: string}>(
    Knisot.getValidRefference,
    canCheckRefference ? `Reference=${refferenceDebounced}&Grower=${growerID}` : ''
  )
  const isSameAsOwn = fetchedKtisa?.Reference === +refference
  const hasRefferenceConflict = !!dateRefference?.Date && !!refference && !isSameAsOwn
  const isShowRecRef = isRequiredRef ? !!refference && !hasRefferenceConflict : !hasRefferenceConflict

  const handleRemark = (e: string) => setRemark(e)

  const handleCreateTitle = () => {
    const data: IEntrieDetails = {
      Details: [],
      EntryDate: format(date, 'yyyyMMdd'),
      EntryNum: 0,
      EntryTime: time.split(':').join('') + '00',
      Grower: growerID,
      GrowerName: '',
      OrderNum: 0,
      Reference: refference.toString().length > 8 ? +refference.toString().slice(0, 8) : +refference,
      Transport: carNum,
      Tranzit: warehouseID || defaultWarehouse,
      Remark: remark,
      LeadName: leadName,
      TypeDoc: teudaTypeID
    }
    if(!!fetchedKtisa) {
      changeTitle({
        ...fetchedKtisa,
        Reference: refference.toString().length > 8 ? +refference.toString().slice(0, 8) : +refference,
      })
    } else {
      createTitle(data)
    }
  }

  const checkForAllowSave = () => {
    return (
      growerID !== 0 &&
      (isRequiredCar ? carID !== 0 : true) &&
      (isRequiredWarehouse ? warehouseID !== 0 : true) &&
      isShowRecRef &&
      !!time
    )
  }

  useEffect(() => {
    const car = carList.find(car => car.CarID === carID)
    if(car) {
      setLeadName(car.DriverName.trim())
      setCarNum(car.CarNum)
    }
  }, [carID, carList])

  const handleChangeTime = (e: string, valid?: boolean) => {
    if(!valid) return
    setTime(e)
  }

  // BM 668: non-zero value = grower-group id — narrow the selector options only;
  // growerObj keeps resolving from the full list so a preset grower outside the
  // group stays visible and saveable.
  const growerGroupID = toGroupID(getBrodMigdal({ id: 668 }))
  const growersForSelect = useMemo(
    () => filterByGroupID(growerList, growerGroupID),
    [growerList, growerGroupID]
  )

  const growerObj = useMemo(
    () => growerList.find(g => g.GrowerID === growerID) ?? null,
    [growerList, growerID]
  )
  const carObj = useMemo(
    () => carList.find(c => c.CarID === carID) ?? null,
    [carList, carID]
  )
  const warehouseObj = useMemo(
    () => warehouseList.find(w => w.WarehouseID === warehouseID) ?? null,
    [warehouseList, warehouseID]
  )
  const teudaTypeObj = useMemo(
    () => teudaTypes.find(tt => tt.SerialID === teudaTypeID) ?? null,
    [teudaTypes, teudaTypeID]
  )

  const isLocked = !!fetchedKtisa
  const refferenceError = hasRefferenceConflict
    ? t('knisa_create_modal_title_referenceExistsForDate', { date: convertToDate(dateRefference!.Date) })
    : undefined

  const isAllowed = checkForAllowSave()

  return (
    <FormShell>
      <FormGrid>
        <FormField label={t('knisa_create_modal_title_date')}>
          <DateField
            selected={date}
            onChange={(d) => d && setDate(d)}
            disabled={isLocked}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_title_time')}>
          <TimeField value={time} onChange={handleChangeTime} />
        </FormField>

        <FormField label={t('knisa_create_modal_title_grower')} required={!growerID}>
          <GrowerSelector
            growers={growersForSelect}
            value={growerObj}
            onSelect={(g) => setGrowerID(g?.GrowerID ?? 0)}
            disabled={isLocked}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_title_refference')}>
          <TextInput
            type='number'
            value={refference || ''}
            onChange={(e) => setRefference(+e.target.value)}
            error={refferenceError}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_title_carSelect')}>
          <CarSelector
            cars={carList}
            value={carObj}
            onSelect={(c) => setCarID(c?.CarID ?? 0)}
            disabled={isLocked}
            allowClear
            clearLabel={t('knisa_create_modal_title_carClear')}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_title_carNum')}>
          <TextInput
            type='number'
            value={carNum || ''}
            onChange={(e) => setCarNum(+e.target.value)}
            disabled={isLocked || carID !== 0}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_title_driver')}>
          <TextInput
            type='text'
            value={leadName}
            onChange={(e) => setLeadName(e.target.value)}
            disabled={isLocked}
          />
        </FormField>

        <FormField
          label={t('knisa_create_modal_title_warehouse')}
          required={isRequiredWarehouse && !warehouseID}
        >
          <WarehouseSelector
            warehouses={warehouseList}
            value={warehouseObj}
            onSelect={(w) => setWarehouseID(w?.WarehouseID ?? 0)}
            disabled={isLocked}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_title_teudaType')}>
          <TeudaTypeSelector
            types={teudaTypes}
            value={teudaTypeObj}
            onSelect={(tt) => setTeudaTypeID(tt?.SerialID ?? 0)}
            disabled={isLocked}
          />
        </FormField>

        <FormField label={t('knisa_create_modal_title_remark')} fullWidth>
          <RemarkArea
            value={remark}
            onChange={(e) => handleRemark(e.target.value)}
            placeholder={t('knisa_create_modal_title_remark')}
            disabled={isLocked}
            rows={3}
          />
        </FormField>
      </FormGrid>

      <FormFooter>
        <AccentButton
          type='button'
          onClick={handleCreateTitle}
          disabled={!isAllowed}
          fullWidth={false}
        >
          {t('knisa_create_modal_title_continue')}
        </AccentButton>
      </FormFooter>
    </FormShell>
  )
}

export default ModalKnisaTitle

const RemarkArea = styled.textarea`
  width: 100%;
  min-height: 5rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 0.875rem;
  padding: 0.75rem 0.875rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  resize: vertical;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.line2};
    cursor: not-allowed;
    opacity: 0.7;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

